import {CABECERA_IDIOMA, ErrorApiSchema} from '@cnc/contracts';
import type {ZodType} from 'zod';
import {idiomaActual, tr} from '../i18n/idioma';

// Error que ve la interfaz. `mensaje` ya viene en espanol llano desde la API
// o se compone aqui cuando ni siquiera hubo respuesta.
export class ErrorDeApi extends Error {
  constructor(
    readonly codigo: string,
    mensaje: string,
    readonly id?: string,
    readonly detalles?: string[],
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }
}

// Avisa cuando el servidor dice que ya no hay sesion (vencio o la cerraron),
// para volver a la pantalla de ingreso desde cualquier parte.
export const avisosDeSesion = new EventTarget();
export const EVENTO_SESION_PERDIDA = 'sesion-perdida';

// Unica puerta de entrada a la API desde el front: siempre valida la respuesta
// con el contrato compartido, para que un cambio en el servidor no pase
// desapercibido. La sesion viaja en una cookie del mismo sitio; el idioma, en
// una cabecera (la API responde mensajes y etiquetas en ese idioma).
// Opciones de una peticion con cuerpo JSON (POST, PUT, PATCH, DELETE).
export const json = (method: string, cuerpo?: unknown): RequestInit => ({
  method,
  headers: {'content-type': 'application/json'},
  ...(cuerpo === undefined ? {} : {body: JSON.stringify(cuerpo)}),
});

export async function pedir<T>(ruta: string, esquema: ZodType<T>, init: RequestInit = {}): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api/${ruta.replace(/^\/+/, '')}`, {
      ...init,
      credentials: 'same-origin',
      headers: {accept: 'application/json', [CABECERA_IDIOMA]: idiomaActual(), ...(init.headers ?? {})},
    });
  } catch {
    throw new ErrorDeApi(
      'SIN_CONEXION',
      tr(
        'No se pudo conectar con el servidor. Revisa que la aplicación esté abierta.',
        'Could not connect to the server. Check that the application is running.',
      ),
    );
  }

  const cuerpo: unknown = await respuesta.json().catch(() => null);

  if (!respuesta.ok) {
    const error = ErrorApiSchema.safeParse(cuerpo);
    if (error.success) {
      if (error.data.codigo === 'NO_AUTORIZADO' && !ruta.startsWith('sesion/')) {
        avisosDeSesion.dispatchEvent(new Event(EVENTO_SESION_PERDIDA));
      }
      throw new ErrorDeApi(error.data.codigo, error.data.error, error.data.id, error.data.detalles);
    }
    throw new ErrorDeApi(
      'DESCONOCIDO',
      tr(
        `El servidor respondió con un error (${respuesta.status}).`,
        `The server responded with an error (${respuesta.status}).`,
      ),
    );
  }

  const datos = esquema.safeParse(cuerpo);
  if (!datos.success)
    throw new ErrorDeApi(
      'RESPUESTA_INVALIDA',
      tr(
        'La respuesta del servidor no tiene el formato esperado.',
        'The server response does not have the expected format.',
      ),
    );
  return datos.data;
}
